import { useCallback, useEffect, useRef } from 'react';
import { newLandingSchema } from '../supabaseClient';
import { audioPlayer } from './audioPlayer';

// Fetches new_landing.exercise_strings once and caches it for the page's
// lifetime (same pattern as useWordsData) — indexed by `text` because the
// exercise components only ever had the raw Russian string on hand (they
// were built around SpeechSynthesisUtterance, not a key), never the row key.
let cachePromise = null;

async function loadExerciseStrings() {
  const schema = newLandingSchema();
  const { data, error } = await schema.from('exercise_strings').select('*');
  if (error) throw new Error(error.message);

  const byText = {};
  for (const row of data) byText[row.text] = row;
  return byText;
}

function getCache() {
  if (!cachePromise) cachePromise = loadExerciseStrings();
  return cachePromise;
}

// Replacement for the exercises' old `new SpeechSynthesisUtterance(text) +
// speech.speak(...)` pattern for the fixed instruction/question strings that
// now have pre-recorded voiceover in the DB. Usage mirrors the old code:
//   playInstruction(STATIC_TEXT, () => { ...what used to be utterance.onend... });
// Text not found in the DB (shouldn't happen for the migrated strings, but
// cheap insurance against a typo/drift) falls through to onEnd immediately
// so the exercise never hangs, with a console.error to flag the mismatch.
export function useInstructionAudio() {
  // Bumped on every play()/stop() call. play() waits on the async
  // getCache() lookup before it can actually start audio — if stop() (or a
  // newer play()) happens before that resolves, the stale continuation must
  // not go on to call audioPlayer.play() or onEnd. Without this, React
  // StrictMode's dev-only double-invoke of a mounting effect (mount →
  // cleanup → mount, once, on a component's *initial* mount) queues two
  // overlapping play() calls for the same text; both eventually resolve and
  // call audioPlayer.play(), and the second's internal stop() interrupts the
  // first mid-start, which can leave neither clip reliably audible — this
  // is exactly why only ever the very first word of a fresh mount (not
  // later ones, which aren't double-invoked) went silent.
  const sessionRef = useRef(0);

  useEffect(() => {
    getCache().catch(() => {}); // prewarm; errors are surfaced when play() is actually called
  }, []);

  const play = useCallback((text, onEnd) => {
    sessionRef.current += 1;
    const mySession = sessionRef.current;
    const isCurrent = () => sessionRef.current === mySession;

    getCache()
      .then((byText) => {
        if (!isCurrent()) return;
        const row = byText[text];
        if (!row || !row.audio_url) {
          console.error(`[instructionAudio] no audio_url for text: "${text}"`);
          onEnd?.();
          return;
        }
        return audioPlayer.play(row.audio_url).then(
          () => { if (isCurrent()) onEnd?.(); },
          (error) => {
            if (!isCurrent()) return;
            console.error(`[instructionAudio] playback failed for "${text}":`, error);
            onEnd?.();
          }
        );
      })
      .catch((error) => {
        if (!isCurrent()) return;
        console.error('[instructionAudio] failed to load exercise_strings:', error);
        onEnd?.();
      });
  }, []);

  const stop = useCallback(() => {
    sessionRef.current += 1;
    audioPlayer.stop();
  }, []);

  return { play, stop };
}
